import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ContractState, ContractType } from '@received/pricing-model';
import { Table } from '../../../components';
import { Sort, SortOrder } from '../../../types/generalTypes';
import { resellerAgreementsTabTableHeader } from './ResellerAgreementsTab.utils';
import { httpService } from '../../../services/httpService/httpService';
import { PathsConfig } from '../../../services/httpService/configPaths';
import { successErrorMassageOptions } from '../../../components/SuccessErrorModal/SuccessErrorModal.utils';
import { ContractTabDataRes, ContractView } from '../../../types/contractTypes';
import { setOpenSuccessErrorModal } from '../../../storeSlices/errorSuccessSlice';
import { Store } from '../../../types/storeTypes';
import { contractMenuOptions } from '../../../utils/ContractUtils';
import { SidebarType } from '../../../components/Sidebars/Sidebars.utils';
import { CustomerTabProps } from '../CustomerTabs.utils';
import { createContract } from '../../../pages/Contracts/ContractComponent/Contract.utils';
import styles from './ResellerAgreementsTab.module.scss';

const defaultNumberOfItems = '20';

interface ResellerAgreementsTabProps extends CustomerTabProps {
	contractType: ContractType;
}

export const ResellerAgreementsTab = ({
	isActive,
	customerData,
	sideBarFilters,
	refreshTabs,
	contractType,
	setOpenSideBarById,
}: ResellerAgreementsTabProps) => {
	const { t } = useTranslation('translation');
	const { toastNotification } = useSelector((store: Store) => store.general);

	const [contractList, setContractList] = useState<ContractView[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [pageNumber, setPageNumber] = useState(0);
	const [states, setStates] = useState<ContractState[]>([]);
	const [numberOfItems, setNumberOfItems] = useState(defaultNumberOfItems);
	const [sort, setSort] = useState<Sort>({ orderBy: 'state', order: SortOrder.DESC });

	const dispatch = useDispatch();
	const location: any = useLocation();
	const navigate = useNavigate();

	useEffect(() => {
		isActive && customerData?.id && getContractTabData();
	}, [
		customerData,
		customerData?.id,
		numberOfItems,
		pageNumber,
		states,
		sort.orderBy,
		sort.order,
		sideBarFilters.filterData,
		sideBarFilters.filterTags,
		toastNotification.showToastNotification,
		isActive,
		refreshTabs,
	]);

	useEffect(() => {
		location?.state?.filterByState && setSort((prev) => ({ ...prev, orderBy: 'state' }));
	}, []);

	const getContractTabData = async (page?: number, items?: string) => {
		try {
			const res: ContractTabDataRes = (
				await httpService({
					dispatch,
					path: PathsConfig.getContractsView,
					params: {
						customerId: customerData?.id,
						pageSize: parseInt(items || numberOfItems),
						pageNumber: page || pageNumber,
						orderBy: sort.orderBy,
						order: sort.orderBy ? sort.order : undefined,
						states: states.length ? states : undefined,
						contractType: ContractType.RESELLER,
						...sideBarFilters?.filterData,
					},
				})
			).data;

			setTotalCount(res?.count);
			setContractList(res?.entries);
		} catch (error) {
			dispatch(setOpenSuccessErrorModal({ responseError: error, successErrorMassage: successErrorMassageOptions.ERROR.FAILED_LOAD_CONTRACT_TAB }));
		}
	};

	const onHideShowDrafts = (showDraft: boolean) => {
		showDraft ? setStates([]) : setStates([ContractState.ACTIVE]);
	};

	const onPressNewContract = async () => {
		const contractId = await createContract(dispatch, contractType, customerData?.id);
		contractId && navigate('../reseller-agreement', { state: { contractId, customer: customerData } });
	};

	return (
		<div className={styles.container}>
			<Table
				sort={sort}
				showSwitch
				onToggleSwitch={onHideShowDrafts}
				header={resellerAgreementsTabTableHeader}
				rows={contractList}
				optionsList={(row) =>
					contractMenuOptions(row, t, dispatch, () => navigate('../reseller-agreement', { state: { contractId: row?.id } }), getContractTabData)
				}
				className={styles.tableContainer}
				numberOfPages={Math.ceil(totalCount / +numberOfItems)}
				totalNumberOfItems={totalCount}
				numberOfItems={numberOfItems}
				onUpdateNumberOfItems={setNumberOfItems}
				onUpdatePageNumber={setPageNumber}
				onRowPress={(data) => setOpenSideBarById?.({ sideBarType: SidebarType.CONTRACT, id: data.id })}
				onFlipOrder={(accessor) => setSort({ orderBy: accessor, order: sort.order === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC })}
				emptyStateProps={{
					title: t('noResellerAgreements'),
					description: t('noContractsDescription'),
					buttonText: `${t('createResellerAgreement')} +`,
					onClick: () => onPressNewContract(),
				}}
			/>
		</div>
	);
};
